"use client";

import type React from "react";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { CustomTabs as Tabs, CustomTabsList as TabsList, CustomTabsTrigger as TabTrigger, CustomTabsContent as TabContent } from "@/components/ui/custom-tabs";
import { Star, BarChart2, Zap, TrendingUp, CreditCard } from "lucide-react";
import { ConnectionStatus } from "@/services/trade/ws-manager";
import type { Symbol } from "@/store/trade/use-binary-store";
import { useTranslations } from "@/hooks/use-translations";
import { marketService } from "@/services/trade/market-service";
import { tickersWs } from "@/services/tickers-ws";
import { marketDataWs, MarketDataSubscription, TickerData as MarketTickerData } from "@/services/trade/market-data-ws";
import { wishlistService } from "@/services/trade/wishlist-service";
import { useExtensionChecker } from "@/lib/extensions";

// Import components
import { SearchBar } from "./search-bar";
import { ColumnHeaders } from "./column-headers";
import { MarketList } from "./market-list";
import { WatchlistEmptyState } from "./watchlist-empty-state";
import { WatchlistHeader } from "./watchlist-header";

// Import types and utils
import type {
  MarketsPanelProps,
  SortField,
  SortDirection,
  Market,
  FuturesMarket,
  TickerData,
} from "./types";
import type { YahooFinanceQuotesResponse } from "@/types/yahoo-finance";
import { formatPrice, formatVolume } from "./utils";

export default function MarketsPanel({
  onMarketSelect,
  currentSymbol = "BTC/USDT",
  defaultMarketType = "spot",
  pinnedMarkets = [],
}: MarketsPanelProps) {
  const t = useTranslations("trade/components/markets/markets-panel");
  const { isExtensionAvailable, extensions } = useExtensionChecker();
  const [searchQuery, setSearchQuery] = useState("");
  const [spotSelectedMarket, setSpotSelectedMarket] =
    useState<Symbol>(currentSymbol);
  const [futuresSelectedMarket, setFuturesSelectedMarket] =
    useState<Symbol>(currentSymbol);
  const [stocksSelectedMarket, setStocksSelectedMarket] =
    useState<Symbol>(currentSymbol);
  const [feedSelectedMarket, setFeedSelectedMarket] =
    useState<Symbol>(currentSymbol);
  const [watchlist, setWatchlist] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState("markets");
  const [marketType, setMarketType] = useState<"spot" | "futures" | "stocks" | "feed">(
    defaultMarketType as "spot" | "futures" | "stocks" | "feed"
  );
  const [markets, setMarkets] = useState<any[]>([]);
  const [futuresMarkets, setFuturesMarkets] = useState<FuturesMarket[]>([]);
  const [stocksMarkets, setStocksMarkets] = useState<any[]>([]);
  const [feedMarkets, setFeedMarkets] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFuturesLoading, setIsFuturesLoading] = useState(true);
  const [isStocksLoading, setIsStocksLoading] = useState(true);
  const [isFeedLoading, setIsFeedLoading] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>(
    ConnectionStatus.DISCONNECTED
  );

  // Ticker data states
  const [spotData, setSpotData] = useState<Record<string, TickerData>>({});
  const [ecoData, setEcoData] = useState<Record<string, TickerData>>({});
  const [futuresData, setFuturesData] = useState<Record<string, TickerData>>({});
  const [stocksData, setStocksData] = useState<Record<string, TickerData>>({});
  const [feedData, setFeedData] = useState<Record<string, TickerData>>({});

  // Advanced market data for active market (like trading header)
  const [activeMarketData, setActiveMarketData] = useState<MarketTickerData | null>(null);
  const activeMarketUnsubscribeRef = useRef<(() => void) | null>(null);

  // Replace the single sort field and direction with an array of sort criteria
  const [sortCriteria, setSortCriteria] = useState<
    Array<{ field: SortField; direction: SortDirection }>
  >([{ field: "name", direction: "asc" }]);

  // Note: Service instances are imported as singletons

  // Fetch stock quotes with rate limiting (15-minute cache)
  const fetchStockQuotes = async (symbols: string[]) => {
    if (symbols.length === 0) {
      setIsStocksLoading(false);
      return;
    }

    try {
      console.log(`Fetching quotes for ${symbols.length} stocks:`, symbols);

      // Resolve API base for both browser and SSR contexts
      const envBase = (
        (import.meta.env?.VITE_API_URL as string | undefined) ||
        ""
      ).replace(/\/$/, "");

      let apiBase: string;
      if (typeof window !== "undefined") {
        const origin = window.location.origin;
        if (
          envBase &&
          /^(https?:\/\/)(localhost|127\.0\.0\.1)/.test(envBase) &&
          !/^(https?:\/\/)(localhost|127\.0\.0\.1)/.test(origin)
        ) {
          apiBase = origin;
        } else {
          apiBase = envBase || origin;
        }
      } else {
        apiBase = envBase || "https://otc.network";
      }

      // Split into batches of 10 symbols to avoid rate limiting
      const batchSize = 10;
      const batches: string[][] = [];
      for (let i = 0; i < symbols.length; i += batchSize) {
        batches.push(symbols.slice(i, i + batchSize));
      }

      const allQuotes: Record<string, any> = {};

      // Process batches with delays
      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        const symbolsParam = batch.join(',');

        try {
          const response = await fetch(`${apiBase}/api/yahoo-finance/market/quotes?symbols=${encodeURIComponent(symbolsParam)}&maxAgeMinutes=15`);
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const text = await response.text();
          if (!text) {
            throw new Error('Empty response from API');
          }
          
          let data: YahooFinanceQuotesResponse;
          try {
            data = JSON.parse(text) as YahooFinanceQuotesResponse;
          } catch (parseError) {
            console.error(`JSON parse error for batch ${i + 1}:`, parseError);
            console.error('Response text:', text);
            console.error('Response status:', response.status);
            console.error('Response headers:', Object.fromEntries(response.headers.entries()));
            
            // Skip this batch and continue with others
            console.warn(`Skipping batch ${i + 1} due to JSON parse error`);
            continue;
          }

          if (data.quotes) {
            Object.assign(allQuotes, data.quotes);
          }

          console.log(`Fetched quotes for batch ${i + 1}/${batches.length}:`, batch);
        } catch (error) {
          console.error(`Error fetching quotes for batch ${i + 1}:`, error);
        }

        // Add delay between batches (except for the last one)
        if (i < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 2000)); // 2 second delay
        }
      }

      // Update stocks with fetched quotes
      if (Object.keys(allQuotes).length > 0) {
        setStocksMarkets(prevStocks =>
          prevStocks.map(stock => {
            const quote = allQuotes[stock.symbol];
            if (quote) {
              return {
                ...stock,
                price: quote.price,
                rawPrice: quote.price,
                change: `${quote.changePercent?.toFixed(2)}%`,
                rawChange: quote.changePercent || 0,
                volume: quote.volume?.toString(),
                rawVolume: quote.volume || 0,
                isPositive: (quote.changePercent || 0) >= 0,
                hasData: true,
                exchange: quote.exchange || stock.exchange // Update exchange from quote or keep existing
              };
            }
            return stock;
          })
        );
      }

    } catch (error) {
      console.error('Error fetching stock quotes:', error);
    } finally {
      setIsStocksLoading(false);
    }
  };

  // Fetch Revolut X tickers
  const fetchFeedTickers = async () => {
    try {
      const envBase = (
        (import.meta.env?.VITE_API_URL as string | undefined) ||
        ""
      ).replace(/\/$/, "");

      let apiBase: string;
      if (typeof window !== "undefined") {
        const origin = window.location.origin;
        if (
          envBase &&
          /^(https?:\/\/)(localhost|127\.0\.0\.1)/.test(envBase) &&
          !/^(https?:\/\/)(localhost|127\.0\.0\.1)/.test(origin)
        ) {
          apiBase = origin;
        } else {
          apiBase = envBase || origin;
        }
      } else {
        apiBase = envBase || "https://otc.network";
      }

      const response = await fetch(`${apiBase}/api/revolut-x/tickers`);
      if (!response.ok) {
        // Silently ignore 401 (missing API key) — feed tickers are optional
        if (response.status === 401) return;
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const tickers: Record<string, TickerData> = {};

      if (Array.isArray(data)) {
        data.forEach((ticker: any) => {
          const symbol = ticker.symbol || ticker.pair;
          if (symbol) {
            // Normalize symbol: BTC-USD → BTC/USD
            const normalizedSymbol = symbol.replace(/-/g, "/");
            tickers[normalizedSymbol] = {
              last: ticker.last || ticker.mid || ticker.bid || 0,
              change: ticker.change_24h || 0,
              quoteVolume: ticker.volume_24h || 0,
            };
          }
        });
      }

      setFeedData(tickers);
    } catch (error) {
      console.error("Error fetching Mkts tickers:", error);
    }
  };

  // Fetch Revolut X trading pairs
  const fetchFeedMarkets = async () => {
    try {
      setIsFeedLoading(true);
      const envBase = (
        (import.meta.env?.VITE_API_URL as string | undefined) ||
        ""
      ).replace(/\/$/, "");

      let apiBase: string;
      if (typeof window !== "undefined") {
        const origin = window.location.origin;
        if (
          envBase &&
          /^(https?:\/\/)(localhost|127\.0\.0\.1)/.test(envBase) &&
          !/^(https?:\/\/)(localhost|127\.0\.0\.1)/.test(origin)
        ) {
          apiBase = origin;
        } else {
          apiBase = envBase || origin;
        }
      } else {
        apiBase = envBase || "https://otc.network";
      }

      const response = await fetch(`${apiBase}/api/revolut-x/pairs`);
      if (!response.ok) {
        // Silently ignore 401 (missing API key) — feed markets are optional
        if (response.status === 401) return;
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const pairs: any[] = [];

      // Revolut X returns an object with symbol keys (e.g., "BTC/USD": {...})
      if (typeof data === 'object' && !Array.isArray(data)) {
        Object.entries(data).forEach(([symbol, pair]: [string, any]) => {
          const normalizedSymbol = symbol.replace(/\//g, "/");
          const currency = pair.base || symbol.split("/")[0];
          const quote = pair.quote || symbol.split("/")[1] || "USD";

          pairs.push({
            symbol: normalizedSymbol,
            displaySymbol: normalizedSymbol,
            currency: currency.toUpperCase(),
            pair: quote.toUpperCase(),
            metadata: {
              precision: {
                price: 2,
                amount: 6,
              },
            },
            isTrending: false,
            isHot: false,
            exchange: "Mkts",
            isFeed: true,
          });
        });
      }

      setFeedMarkets(pairs);
      await fetchFeedTickers();
      setIsFeedLoading(false);
    } catch (error) {
      console.error("Error fetching Mkts markets:", error);
      setIsFeedLoading(false);
    }
  };

  // Update selected market when currentSymbol prop changes
  useEffect(() => {
    if (currentSymbol) {
      // Update the appropriate selected market based on the current market type
      if (marketType === "spot") {
        setSpotSelectedMarket(currentSymbol);
      } else if (marketType === "futures") {
        setFuturesSelectedMarket(currentSymbol);
      } else if (marketType === "stocks") {
        setStocksSelectedMarket(currentSymbol);
      } else if (marketType === "feed") {
        setFeedSelectedMarket(currentSymbol);
      }
    }
  }, [currentSymbol, marketType]);

  // Update the useEffect at the beginning to check for market type in URL
  useEffect(() => {
    // Check URL for market parameters on component mount
    const urlParams = new URLSearchParams(window.location.search);
    const typeParam = urlParams.get("type");
    const symbolParam = urlParams.get("symbol");

    // If no query parameters, set defaults and update URL
    if (!typeParam && !symbolParam && window.location.pathname === "/trade") {
      // Set defaults - changed to "spot" as default
      setMarketType("spot");
      setActiveTab("markets");
      setSpotSelectedMarket("BTC/USDT");

      // Update URL with defaults
      const url = `${window.location.pathname}?symbol=BTC-USDT&type=spot`;
      window.history.replaceState({ path: url }, "", url);

      if (onMarketSelect) {
        onMarketSelect("BTC/USDT", "spot");
      }
    }
    // Otherwise use parameters from URL if available
    else {
      // Set market type if present in URL
      if (typeParam === "spot" || typeParam === "futures" || typeParam === "stocks") {
        setMarketType(typeParam);
        setActiveTab(
          typeParam === "spot" ? "markets" :
          typeParam === "futures" ? "futures" :
          "stocks"
        );
      } else {
        // If no type parameter, default to spot
        setMarketType("spot");
        setActiveTab("markets");
      }

      // Set selected market if present in URL
      if (symbolParam) {
        // Symbol format is XXX-YYY, we need to convert to XXX/YYY for internal use
        const [currency, pair] = symbolParam.split("-");
        if (currency && pair) {
          const fullSymbol = `${currency}/${pair}` as Symbol;

          // Update the appropriate selected market based on the market type
          if (typeParam === "spot" || !typeParam) {
            setSpotSelectedMarket(fullSymbol);
          } else if (typeParam === "futures") {
            setFuturesSelectedMarket(fullSymbol);
          } else if (typeParam === "stocks") {
            setStocksSelectedMarket(fullSymbol);
          }
        }
      }
    }
  }, [onMarketSelect]);

  // Ensure the useEffect for wishlist subscription is properly implemented
  useEffect(() => {
    const unsubscribe = wishlistService.subscribe((items) => {
      setWatchlist(items.map((item) => item.symbol));
    });

    return () => unsubscribe();
  }, []);

  // Listen for market switching cleanup events
  useEffect(() => {
    const handleMarketSwitchingCleanup = (event: CustomEvent) => {
      const { oldSymbol, newSymbol, oldMarketType, newMarketType } = event.detail;
      
      // Clean up active market data
      setActiveMarketData(null);
      
      // Clean up current subscription
      if (activeMarketUnsubscribeRef.current) {
        activeMarketUnsubscribeRef.current();
        activeMarketUnsubscribeRef.current = null;
      }
      
      // Clear ticker data for old market type to prevent contamination
      if (oldMarketType === "spot") {
        setSpotData(prev => {
          const newData = { ...prev };
          delete newData[oldSymbol];
          return newData;
        });
      } else if (oldMarketType === "futures") {
        setFuturesData(prev => {
          const newData = { ...prev };
          delete newData[oldSymbol];
          return newData;
        });
      }
    };

    window.addEventListener('market-switching-cleanup', handleMarketSwitchingCleanup as EventListener);
    
    return () => {
      window.removeEventListener('market-switching-cleanup', handleMarketSwitchingCleanup as EventListener);
    };
  }, []);

  // Format futures markets - Advanced: Use market data WS for active market
  const formattedFuturesMarkets = useMemo((): Market[] => {

    
    return futuresMarkets.map((market) => {
      // Use consistent currency/pair format for both symbol and ticker key
      const symbol = `${market.currency}/${market.pair}` as Symbol;
      const tickerKey = symbol;
      

      
      // Check if this is the currently active futures market
      const isActiveMarket = symbol === futuresSelectedMarket && marketType === "futures";
      
      let tickerData: any;

      if (isActiveMarket && activeMarketData) {
        // ⚡ Use high-frequency market data for active futures market
        tickerData = {
          last: activeMarketData.last || activeMarketData.close,
          change: activeMarketData.change,
          percentage: activeMarketData.percentage,
          quoteVolume: activeMarketData.quoteVolume || activeMarketData.baseVolume,
          fundingRate: futuresData[tickerKey]?.fundingRate || 0, // Get funding rate from futures ticker
        };
      } else {
        // Use regular futures ticker data for non-active markets
        tickerData = futuresData[tickerKey] || {
          last: 0,
          change: 0,
          quoteVolume: 0,
          fundingRate: 0,
        };
      }

      // For futures markets, show the market even if no trading activity (last = 0)
      // This allows users to see and select markets that don't have trades yet
      const hasData = !!tickerData.last || tickerData.symbol === symbol;
      const metadata =
        typeof market.metadata === "string"
          ? JSON.parse(market.metadata)
          : market.metadata || { precision: { price: 2, amount: 2 } };

      // Parse leverage from metadata - handle both string and object formats
      let maxLeverage = 1;
      if (metadata.limits?.leverage) {
        if (typeof metadata.limits.leverage === "string") {
          // Handle string format like "1,20,50,125"
          const leverageOptions = metadata.limits.leverage.split(",").map(Number);
          maxLeverage = Math.max(...leverageOptions);
        } else if (typeof metadata.limits.leverage === "object" && metadata.limits.leverage.max) {
          // Handle object format with max property
          maxLeverage = metadata.limits.leverage.max;
        } else if (typeof metadata.limits.leverage === "number") {
          // Handle direct number
          maxLeverage = metadata.limits.leverage;
        }
      }

      return {
        symbol,
        displaySymbol: `${market.currency}/${market.pair}`,
        currency: market.currency,
        pair: market.pair,
        price: tickerData.last ? formatPrice(tickerData.last, metadata) : "0.00",
        rawPrice: tickerData.last || 0,
        change: tickerData.last ? (tickerData.change || 0).toFixed(2) + "%" : "0.00%",
        rawChange: tickerData.change || 0,
        volume: tickerData.last ? formatVolume(tickerData.quoteVolume || 0) : "0.00",
        rawVolume: tickerData.quoteVolume || 0,
        isPositive: (tickerData.change || 0) >= 0,
        isTrending: market.isTrending || false,
        isHot: market.isHot || false,
        metadata,
        hasData,
        fundingRate: (tickerData.fundingRate || 0).toFixed(4) + "%",
        rawFundingRate: Number(tickerData.fundingRate || 0),
        leverage: maxLeverage,
      };
    });
  }, [futuresMarkets, futuresData, activeMarketData, futuresSelectedMarket, marketType]);

  // Format markets with live data - Advanced: Use market data WS for active market
  const formattedMarkets = useMemo((): Market[] => {
    const mergedMarkets = [...pinnedMarkets, ...markets].reduce<any[]>((acc, market) => {
      const key = market.symbol || `${market.currency}/${market.pair}`;
      if (!key || acc.some((existing) => (existing.symbol || `${existing.currency}/${existing.pair}`) === key)) {
        return acc;
      }
      acc.push(market);
      return acc;
    }, []);

    return mergedMarkets.map((market) => {
      // Use symbol from market data (already in currency/pair format)
      const symbol = market.symbol;
      const tickerKey = symbol;
      
      // Check if this is the currently active market
      const isActiveMarket = symbol === (marketType === "spot" ? spotSelectedMarket : futuresSelectedMarket) && marketType === "spot";
      
      let tickerData: any;
      let isEco = false;

      if (isActiveMarket && activeMarketData) {
        // ⚡ Use high-frequency market data for active market
        tickerData = {
          last: activeMarketData.last || activeMarketData.close,
          change: activeMarketData.change,
          percentage: activeMarketData.percentage,
          quoteVolume: activeMarketData.quoteVolume || activeMarketData.baseVolume,
          high: activeMarketData.high,
          low: activeMarketData.low,
          bid: activeMarketData.bid,
          ask: activeMarketData.ask,
        };
      } else {
        // Use regular ticker data for non-active markets
        // Eco tickers have higher priority over spot tickers
        tickerData = ecoData[tickerKey] || spotData[tickerKey] || {
          last: 0,
          change: 0,
          quoteVolume: 0,
        };
        // Use the isEco flag from the market data (set by the backend)
        isEco = market.isEco || false;
      }

      const metadata = typeof market.metadata === "string"
        ? JSON.parse(market.metadata)
        : market.metadata || {};
      // Only show price from real-time ticker data; never fall back to stale metadata.price_usd
      let lastPrice = tickerData.last || 0;
      // Reject hardcoded mock prices: exactly 50000 with zero change
      if (lastPrice > 0 && Math.abs(lastPrice - 50000) < 0.5 && (tickerData.change || 0) === 0) {
        lastPrice = 0;
      }
      const hasData = lastPrice > 0;

      return {
        symbol: symbol,
        displaySymbol: market.displaySymbol,
        currency: market.currency,
        pair: market.pair || "USDT",
        price: hasData ? formatPrice(lastPrice, market.metadata) : null,
        rawPrice: lastPrice,
        change: hasData ? (tickerData.change || 0).toFixed(2) + "%" : null,
        rawChange: tickerData.change || 0,
        volume: hasData ? formatVolume(tickerData.quoteVolume || 0) : null,
        rawVolume: tickerData.quoteVolume || 0,
        isPositive: (tickerData.change || 0) >= 0,
        isTrending: market.isTrending || false,
        isHot: market.isHot || false,
        isEco,
        metadata: market.metadata || { precision: { price: 2, amount: 2 } },
        hasData,
      };
    });
  }, [markets, pinnedMarkets, spotData, ecoData, activeMarketData, spotSelectedMarket, marketType]);

  // Subscribe to market data for active market (exactly like trading header)
  useEffect(() => {
    const currentActiveSymbol = marketType === "spot" ? spotSelectedMarket : futuresSelectedMarket;
    
    if (!currentActiveSymbol) return;

    // Clean up previous subscription
    if (activeMarketUnsubscribeRef.current) {
      activeMarketUnsubscribeRef.current();
      activeMarketUnsubscribeRef.current = null;
    }

    // Determine the correct market type based on the current context
    // Only subscribe to futures data when we're actually on the futures tab AND have a futures market selected
    const subscriptionMarketType = (marketType === "futures" && currentActiveSymbol === futuresSelectedMarket) ? "futures" : "spot";

    // Skip WebSocket subscription for stocks (they use Yahoo Finance API)
    if (marketType === "stocks") {
      console.log(`[Markets Panel] Stocks market type uses Yahoo Finance API, skipping WebSocket subscription`);
      return;
    }

    // Additional validation: Check if the symbol exists in the appropriate market list
    let isValidSubscription = false;
    if (subscriptionMarketType === "futures") {
      // For futures, check if the symbol exists in futures markets
      isValidSubscription = futuresMarkets.some(m => `${m.currency}/${m.pair}` === currentActiveSymbol);
    } else {
      // For spot, check if the symbol exists in spot markets
      isValidSubscription = markets.some(m => m.symbol === currentActiveSymbol);
    }

    if (!isValidSubscription) {
      console.warn(`[Markets Panel] Skipping subscription for ${currentActiveSymbol} on ${subscriptionMarketType} - symbol not found in ${subscriptionMarketType} markets`);
      return;
    }

    // Subscribe to ticker data for active market
    const unsubscribe = marketDataWs.subscribe<MarketTickerData>(
      {
        symbol: currentActiveSymbol,
        type: "ticker",
        marketType: subscriptionMarketType,
      },
      (tickerData) => {
        setActiveMarketData(tickerData);
      }
    );

    activeMarketUnsubscribeRef.current = unsubscribe;

    return () => {
      if (activeMarketUnsubscribeRef.current) {
        activeMarketUnsubscribeRef.current();
        activeMarketUnsubscribeRef.current = null;
      }
    };
  }, [spotSelectedMarket, futuresSelectedMarket, stocksSelectedMarket, marketType, markets, futuresMarkets]);

  // Handle market selection
  const handleMarketSelect = useCallback(
    (symbol: Symbol) => {
      // Get current selected market for comparison
      let currentSelectedMarket = "";
      if (marketType === "spot") {
        currentSelectedMarket = spotSelectedMarket;
      } else if (marketType === "futures") {
        currentSelectedMarket = futuresSelectedMarket;
      } else if (marketType === "stocks") {
        currentSelectedMarket = stocksSelectedMarket;
      }
      
      // Skip if selecting the same market
      if (symbol === currentSelectedMarket) {
        return;
      }

      // Clean up active market data immediately to prevent showing old data
      setActiveMarketData(null);
      
      // Clean up current market subscription
      if (activeMarketUnsubscribeRef.current) {
        activeMarketUnsubscribeRef.current();
        activeMarketUnsubscribeRef.current = null;
      }

      // Update the appropriate selected market based on the current market type
      if (marketType === "spot") {
        setSpotSelectedMarket(symbol);
      } else if (marketType === "futures") {
        setFuturesSelectedMarket(symbol);
      } else if (marketType === "stocks") {
        setStocksSelectedMarket(symbol);
      } else if (marketType === "feed") {
        setFeedSelectedMarket(symbol);
      }

      if (onMarketSelect) {
        onMarketSelect(symbol, marketType);
      }

      // Find the current market to get currency and pair for URL update
      let currency = "";
      let pair = "";

      if (marketType === "spot") {
        const market = [...pinnedMarkets, ...markets].find((m) => m.symbol === symbol);
        if (market) {
          currency = market.currency;
          pair = market.pair || "USDT";
        }
      } else if (marketType === "futures") {
        const market = futuresMarkets.find((m) => `${m.currency}/${m.pair}` === symbol);
        if (market) {
          currency = market.currency;
          pair = market.pair;
        }
      } else if (marketType === "stocks") {
        // For stocks, the symbol is the ticker (e.g., "AAPL")
        currency = symbol;
        pair = "STOCK";
      } else if (marketType === "feed") {
        const market = feedMarkets.find((m) => m.symbol === symbol);
        if (market) {
          currency = market.currency;
          pair = market.pair || "USD";
        }
      }

      if (
        currency &&
        pair &&
        currency !== "undefined" &&
        pair !== "undefined"
      ) {
        // Update URL with currency-pair format and market type
        const url = `${window.location.pathname}?symbol=${currency}-${pair}&type=${marketType}`;
        window.history.pushState({ path: url }, "", url);
      }
    },
    [marketType, onMarketSelect, markets, pinnedMarkets, futuresMarkets, feedMarkets, spotSelectedMarket, futuresSelectedMarket, stocksSelectedMarket, feedSelectedMarket]
  );

  // Toggle watchlist
  const toggleWatchlist = useCallback(
    (symbol: string, marketType: "spot" | "futures" | "stocks" | "feed", e: React.MouseEvent) => {
      e.stopPropagation();
      wishlistService.toggleWishlist(symbol, marketType);
    },
    []
  );

  useEffect(() => {
    setIsLoading(true);
    setIsFuturesLoading(true);
    setIsStocksLoading(true);

    // Initialize the WebSocket manager
    tickersWs.initialize();

    // Subscribe to market data
    const spotMarketsUnsubscribe = marketService.subscribeToSpotMarkets(
      (markets) => {
        setMarkets(markets);
        setIsLoading(false);
      }
    );

    const futuresMarketsUnsubscribe = marketService.subscribeToFuturesMarkets(
      (markets) => {
        setFuturesMarkets(markets);
        setIsFuturesLoading(false);
      }
    );

    // Try to get cached data immediately
    const cachedSpotMarkets = marketService.getCachedSpotMarkets();
    const cachedFuturesMarkets = marketService.getCachedFuturesMarkets();

    if (cachedSpotMarkets.length > 0) {
      setMarkets(cachedSpotMarkets);
      setIsLoading(false);
    }

    if (cachedFuturesMarkets.length > 0) {
      setFuturesMarkets(cachedFuturesMarkets);
      setIsFuturesLoading(false);
    }

    // If no cached data, fetch from service
    if (cachedSpotMarkets.length === 0) {
      marketService.getSpotMarkets().catch((error) => {
        console.error("Error getting spot markets:", error);
        setIsLoading(false);
      });
    }

    if (cachedFuturesMarkets.length === 0) {
      marketService.getFuturesMarkets().catch((error) => {
        console.error("Error getting futures markets:", error);
        setIsFuturesLoading(false);
      });
    }

    // Fetch popular stocks
    const fetchPopularStocks = async () => {
      try {
        const response = await fetch('/api/yahoo-finance/market/popular-stocks');
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const text = await response.text();
        if (!text) {
          throw new Error('Empty response from API');
        }
        
        let data: { popularStocks: any[] };
        try {
          data = JSON.parse(text) as { popularStocks: any[] };
        } catch (parseError) {
          console.error('JSON parse error for popular stocks:', parseError);
          console.error('Response text:', text);
          console.error('Response status:', response.status);
          console.error('Response headers:', Object.fromEntries(response.headers.entries()));
          
          // Use fallback data instead of throwing error
          console.warn('Using fallback popular stocks due to JSON parse error');
          const fallbackStocks = [
            { symbol: 'AAPL', displaySymbol: 'AAPL', currency: 'USD', pair: 'STOCK', metadata: { precision: { price: 2, amount: 2 } }, isTrending: true, isHot: false, exchange: 'NASDAQ' },
            { symbol: 'MSFT', displaySymbol: 'MSFT', currency: 'USD', pair: 'STOCK', metadata: { precision: { price: 2, amount: 2 } }, isTrending: true, isHot: false, exchange: 'NASDAQ' },
            { symbol: 'AMZN', displaySymbol: 'AMZN', currency: 'USD', pair: 'STOCK', metadata: { precision: { price: 2, amount: 2 } }, isTrending: true, isHot: false, exchange: 'NASDAQ' },
            { symbol: 'NVDA', displaySymbol: 'NVDA', currency: 'USD', pair: 'STOCK', metadata: { precision: { price: 2, amount: 2 } }, isTrending: true, isHot: false, exchange: 'NASDAQ' },
            { symbol: 'GOOGL', displaySymbol: 'GOOGL', currency: 'USD', pair: 'STOCK', metadata: { precision: { price: 2, amount: 2 } }, isTrending: true, isHot: false, exchange: 'NASDAQ' },
            { symbol: '^GSPC', displaySymbol: 'S&P 500', currency: 'USD', pair: 'INDEX', metadata: { precision: { price: 2, amount: 2 } }, isTrending: false, isHot: true, exchange: 'INDEX' },
            { symbol: '^IXIC', displaySymbol: 'NASDAQ', currency: 'USD', pair: 'INDEX', metadata: { precision: { price: 2, amount: 2 } }, isTrending: false, isHot: true, exchange: 'INDEX' },
            { symbol: '^DJI', displaySymbol: 'Dow Jones', currency: 'USD', pair: 'INDEX', metadata: { precision: { price: 2, amount: 2 } }, isTrending: false, isHot: true, exchange: 'INDEX' }
          ];
          
          setStocksMarkets(fallbackStocks);
          setIsStocksLoading(false);
          return;
        }
        
        const popularStocksData = data.popularStocks || [];

        // Convert popular stocks to market format
        const stocksMarketData = popularStocksData.map((stock: any) => ({
          symbol: stock.symbol,
          displaySymbol: stock.symbol,
          currency: 'USD',
          pair: 'STOCK',
          metadata: { precision: { price: 2, amount: 2 } },
          isTrending: stock.category === 'trending',
          isHot: false,
          exchange: stock.exchange || 'NASDAQ' // Add exchange information
        }));

        setStocksMarkets(stocksMarketData);

        // Fetch quotes for stocks (with rate limiting)
        if (stocksMarketData.length > 0) {
          fetchStockQuotes(stocksMarketData.map(stock => stock.symbol));
        } else {
          setIsStocksLoading(false);
        }
      } catch (error) {
        console.error("Error fetching popular stocks:", error);

        // Fallback to some popular stocks
        const fallbackStocks = [
          { symbol: 'AAPL', displaySymbol: 'AAPL', currency: 'USD', pair: 'STOCK', metadata: { precision: { price: 2, amount: 2 } }, isTrending: true, isHot: false, exchange: 'NASDAQ' },
          { symbol: 'MSFT', displaySymbol: 'MSFT', currency: 'USD', pair: 'STOCK', metadata: { precision: { price: 2, amount: 2 } }, isTrending: true, isHot: false, exchange: 'NASDAQ' },
          { symbol: 'AMZN', displaySymbol: 'AMZN', currency: 'USD', pair: 'STOCK', metadata: { precision: { price: 2, amount: 2 } }, isTrending: true, isHot: false, exchange: 'NASDAQ' },
          { symbol: 'NVDA', displaySymbol: 'NVDA', currency: 'USD', pair: 'STOCK', metadata: { precision: { price: 2, amount: 2 } }, isTrending: true, isHot: false, exchange: 'NASDAQ' },
          { symbol: 'GOOGL', displaySymbol: 'GOOGL', currency: 'USD', pair: 'STOCK', metadata: { precision: { price: 2, amount: 2 } }, isTrending: true, isHot: false, exchange: 'NASDAQ' },
          { symbol: '^GSPC', displaySymbol: 'S&P 500', currency: 'USD', pair: 'INDEX', metadata: { precision: { price: 2, amount: 2 } }, isTrending: false, isHot: true, exchange: 'INDEX' },
          { symbol: '^IXIC', displaySymbol: 'NASDAQ', currency: 'USD', pair: 'INDEX', metadata: { precision: { price: 2, amount: 2 } }, isTrending: false, isHot: true, exchange: 'INDEX' },
          { symbol: '^DJI', displaySymbol: 'Dow Jones', currency: 'USD', pair: 'INDEX', metadata: { precision: { price: 2, amount: 2 } }, isTrending: false, isHot: true, exchange: 'INDEX' }
        ];

        setStocksMarkets(fallbackStocks);
        setIsStocksLoading(false);
      }
    };

    fetchPopularStocks();

    // Fetch Revolut X markets
    fetchFeedMarkets();

    // Subscribe to WebSocket data - use direct updates to avoid unnecessary re-renders
    const spotUnsubscribe = tickersWs.subscribeToSpotData((data) => {
      setSpotData(data);
    });

    // Only subscribe to eco data if ecosystem extension is available
    const ecoUnsubscribe = isExtensionAvailable("ecosystem") 
      ? tickersWs.subscribeToEcoData((data) => {
          setEcoData(data);
        })
      : () => {}; // No-op unsubscribe function

    // Only subscribe to futures data if futures extension is available
    const futuresUnsubscribe = isExtensionAvailable("futures")
      ? tickersWs.subscribeToFuturesData((data) => {
          setFuturesData(data);
        })
      : () => {}; // No-op unsubscribe function
    
    const connectionStatusUnsubscribe =
      tickersWs.subscribeToConnectionStatus(setConnectionStatus);

    // Cleanup function
    return () => {
      spotMarketsUnsubscribe();
      futuresMarketsUnsubscribe();
      spotUnsubscribe();
      ecoUnsubscribe();
      futuresUnsubscribe();
      connectionStatusUnsubscribe();
    };
  }, [extensions]);

  // Handle sort toggle with support for multiple criteria
  const handleSort = (field: SortField) => {
    setSortCriteria((prevCriteria) => {
      // Check if this field is already in the criteria
      const existingIndex = prevCriteria.findIndex((c) => c.field === field);

      if (existingIndex >= 0) {
        // Field exists, toggle direction or remove if it's not the primary sort
        const newCriteria = [...prevCriteria];
        if (existingIndex === 0) {
          // Primary sort, toggle direction
          newCriteria[0] = {
            ...newCriteria[0],
            direction: newCriteria[0].direction === "asc" ? "desc" : "asc",
          };
        } else {
          // Secondary sort, remove it
          newCriteria.splice(existingIndex, 1);
        }
        return newCriteria;
      } else {
        // Field doesn't exist in criteria, add it as primary and keep others as secondary
        return [
          { field, direction: "asc" },
          ...prevCriteria.filter((c) => c.field !== field),
        ];
      }
    });
  };

  // Apply multiple sorting criteria to markets
  const sortMarkets = useCallback(
    (markets: Market[]) => {
      return [...markets].sort((a, b) => {
        // Apply each sort criterion in order until we find a difference
        for (const { field, direction } of sortCriteria) {
          const multiplier = direction === "asc" ? 1 : -1;
          let comparison = 0;

          switch (field) {
            case "name":
              comparison = a.displaySymbol.localeCompare(b.displaySymbol);
              break;
            case "price":
              comparison = (a.rawPrice || 0) - (b.rawPrice || 0);
              break;
            case "change":
              comparison = (a.rawChange || 0) - (b.rawChange || 0);
              break;
            case "volume":
              comparison = (a.rawVolume || 0) - (b.rawVolume || 0);
              break;
            case "funding":
              comparison = (a.rawFundingRate || 0) - (b.rawFundingRate || 0);
              break;
          }

          if (comparison !== 0) {
            return multiplier * comparison;
          }
        }

        // If all criteria are equal, default to name
        return a.displaySymbol.localeCompare(b.displaySymbol);
      });
    },
    [sortCriteria]
  );

  // Format stocks markets
  const formattedStocksMarkets = useMemo((): Market[] => {
    return stocksMarkets.map((market) => {
      const symbol = market.symbol;
      const tickerKey = symbol;

      // Check if this is the currently active stocks market
      const isActiveMarket = symbol === stocksSelectedMarket && marketType === "stocks";

      let tickerData: any;

      if (isActiveMarket && activeMarketData) {
        // ⚡ Use high-frequency market data for active stocks market
        tickerData = {
          last: activeMarketData.last || activeMarketData.close,
          change: activeMarketData.change,
          percentage: activeMarketData.percentage,
          quoteVolume: activeMarketData.quoteVolume || activeMarketData.baseVolume,
          high: activeMarketData.high,
          low: activeMarketData.low,
          bid: activeMarketData.bid,
          ask: activeMarketData.ask,
        };
      } else {
        // Use stocks ticker data for non-active markets
        tickerData = stocksData[tickerKey] || {
          last: 0,
          change: 0,
          quoteVolume: 0,
        };
      }

      const hasData = !!tickerData.last;

      return {
        symbol: symbol,
        displaySymbol: market.displaySymbol || symbol,
        currency: market.currency || "USD",
        pair: "STOCK",
        price: hasData ? formatPrice(tickerData.last!, market.metadata) : null,
        rawPrice: tickerData.last || 0,
        change: hasData ? (tickerData.change || 0).toFixed(2) + "%" : null,
        rawChange: tickerData.change || 0,
        volume: hasData ? formatVolume(tickerData.quoteVolume || 0) : null,
        rawVolume: tickerData.quoteVolume || 0,
        isPositive: (tickerData.change || 0) >= 0,
        isTrending: market.isTrending || false,
        isHot: market.isHot || false,
        metadata: market.metadata || { precision: { price: 2, amount: 2 } },
        hasData,
      };
    });
  }, [stocksMarkets, stocksData, activeMarketData, stocksSelectedMarket, marketType]);

  // Format Revolut X markets
  const formattedFeedMarkets = useMemo((): Market[] => {
    return feedMarkets.map((market) => {
      const symbol = market.symbol;
      const tickerKey = symbol;

      const isActiveMarket = symbol === feedSelectedMarket && marketType === "feed";

      let tickerData: any;

      if (isActiveMarket && activeMarketData) {
        tickerData = {
          last: activeMarketData.last || activeMarketData.close,
          change: activeMarketData.change,
          percentage: activeMarketData.percentage,
          quoteVolume: activeMarketData.quoteVolume || activeMarketData.baseVolume,
          high: activeMarketData.high,
          low: activeMarketData.low,
          bid: activeMarketData.bid,
          ask: activeMarketData.ask,
        };
      } else {
        tickerData = feedData[tickerKey] || {
          last: 0,
          change: 0,
          quoteVolume: 0,
        };
      }

      const hasData = !!tickerData.last;

      return {
        symbol: symbol,
        displaySymbol: market.displaySymbol || symbol,
        currency: market.currency || "BTC",
        pair: market.pair || "USD",
        price: hasData ? formatPrice(tickerData.last!, market.metadata) : null,
        rawPrice: tickerData.last || 0,
        change: hasData ? (tickerData.change || 0).toFixed(2) + "%" : null,
        rawChange: tickerData.change || 0,
        volume: hasData ? formatVolume(tickerData.quoteVolume || 0) : null,
        rawVolume: tickerData.quoteVolume || 0,
        isPositive: (tickerData.change || 0) >= 0,
        isTrending: market.isTrending || false,
        isHot: market.isHot || false,
        metadata: market.metadata || { precision: { price: 2, amount: 2 } },
        hasData,
        exchange: "Mkts",
      };
    });
  }, [feedMarkets, feedData, activeMarketData, feedSelectedMarket, marketType]);

  // Filter markets based on search query and market type
  const getFilteredMarkets = useCallback(() => {
    let marketsToFilter: Market[];

    if (marketType === "spot") {
      marketsToFilter = formattedMarkets;
    } else if (marketType === "futures") {
      marketsToFilter = formattedFuturesMarkets;
    } else if (marketType === "stocks") {
      marketsToFilter = formattedStocksMarkets;
    } else if (marketType === "feed") {
      marketsToFilter = formattedFeedMarkets;
    } else {
      marketsToFilter = formattedMarkets;
    }

    const filtered = marketsToFilter.filter(
      (market) =>
        market.displaySymbol
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        market.currency.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return sortMarkets(filtered);
  }, [
    marketType,
    formattedMarkets,
    formattedFuturesMarkets,
    formattedStocksMarkets,
    formattedFeedMarkets,
    searchQuery,
    sortMarkets,
  ]);

  const filteredMarkets = getFilteredMarkets();

  // Update the watchlist markets to include spot, futures, stocks, and revolut-x markets
  const watchlistMarkets = useMemo(() => {
    return sortMarkets([
      ...formattedMarkets
        .filter((market) => wishlistService.isInWishlist(market.symbol, "spot"))
        .map((market) => ({ ...market, type: "spot" as const })),
      ...formattedFuturesMarkets
        .filter((market) =>
          wishlistService.isInWishlist(market.symbol, "futures")
        )
        .map((market) => ({ ...market, type: "futures" as const })),
      ...formattedStocksMarkets
        .filter((market) =>
          wishlistService.isInWishlist(market.symbol, "stocks")
        )
        .map((market) => ({ ...market, type: "stocks" as const })),
      ...formattedFeedMarkets
        .filter((market) =>
          wishlistService.isInWishlist(market.symbol, "feed")
        )
        .map((market) => ({ ...market, type: "feed" as const })),
    ]);
  }, [formattedMarkets, formattedFuturesMarkets, formattedStocksMarkets, formattedFeedMarkets, sortMarkets]);

  // Update the handleTabChange function to also update market type
  const handleTabChange = (value: string) => {
    setActiveTab(value);

    // Clean up active market data when switching tabs to prevent stale data
    setActiveMarketData(null);
    
    // Clean up active market subscription when switching tabs
    if (activeMarketUnsubscribeRef.current) {
      activeMarketUnsubscribeRef.current();
      activeMarketUnsubscribeRef.current = null;
    }

    // Update market type based on tab selection
    if (value === "markets") {
      setMarketType("spot");
    } else if (value === "futures") {
      setMarketType("futures");
    } else if (value === "stocks") {
      setMarketType("stocks");
    } else if (value === "feed") {
      setMarketType("feed");
    }

    // Don't update URL here - only update URL when a market is explicitly selected
  };

  // Get the currently selected market based on the active tab
  const getSelectedMarket = () => {
    if (marketType === "spot") return spotSelectedMarket;
    if (marketType === "futures") return futuresSelectedMarket;
    if (marketType === "stocks") return stocksSelectedMarket;
    if (marketType === "feed") return feedSelectedMarket;
    return spotSelectedMarket;
  };

  return (
    <div className="h-full flex flex-col bg-background dark:bg-zinc-950 overflow-hidden">
      <Tabs
        value={activeTab}
        className="w-full flex flex-col h-full overflow-hidden"
        onValueChange={handleTabChange}
      >
        <TabsList className="w-full grid grid-cols-5">
          <TabTrigger value="watchlist">
            <Star className="h-3 w-3 mr-1" />
            {t("Watchlist")}
          </TabTrigger>
          <TabTrigger value="markets">
            <BarChart2 className="h-3 w-3 mr-1" />
            {t("Spot")}
          </TabTrigger>
          <TabTrigger value="futures">
            <Zap className="h-3 w-3 mr-1" />
            {t("Futures")}
          </TabTrigger>
          <TabTrigger value="stocks">
            <TrendingUp className="h-3 w-3 mr-1" />
            {t("Stocks")}
          </TabTrigger>
          <TabTrigger value="feed">
            <CreditCard className="h-3 w-3 mr-1" />
            Mkts
          </TabTrigger>
        </TabsList>

        <TabContent
          value="markets"
          className="flex flex-col flex-1 overflow-hidden"
        >
          <SearchBar
            placeholder="Search markets..."
            value={searchQuery}
            onChange={setSearchQuery}
          />

          <ColumnHeaders
            leftColumn={{ label: "Symbol", sortField: "name" }}
            rightColumn={{ label: "24h Change", sortField: "change" }}
            sortCriteria={sortCriteria}
            onSort={handleSort}
          />

          <div className="overflow-y-auto flex-1 min-h-0 scrollbar-none">
            <MarketList
              markets={filteredMarkets}
              isLoading={isLoading}
              selectedMarket={spotSelectedMarket}
              onMarketSelect={handleMarketSelect}
              onToggleWatchlist={toggleWatchlist}
              marketType="spot"
              onSortVolume={() => handleSort("volume")}
              onSortPrice={() => handleSort("price")}
            />
          </div>
        </TabContent>

        <TabContent
          value="futures"
          className="flex flex-col flex-1 overflow-hidden"
        >
          <SearchBar
            placeholder="Search futures..."
            value={searchQuery}
            onChange={setSearchQuery}
          />

          <ColumnHeaders
            leftColumn={{ label: "Symbol", sortField: "name" }}
            rightColumn={{ label: "Funding Rate", sortField: "funding" }}
            sortCriteria={sortCriteria}
            onSort={handleSort}
          />

          <div className="overflow-y-auto flex-1 min-h-0 scrollbar-none">
            <MarketList
              markets={filteredMarkets}
              isLoading={isFuturesLoading}
              selectedMarket={futuresSelectedMarket}
              onMarketSelect={handleMarketSelect}
              onToggleWatchlist={toggleWatchlist}
              marketType="futures"
              onSortVolume={() => handleSort("volume")}
              onSortPrice={() => handleSort("price")}
            />
          </div>
        </TabContent>

        <TabContent
          value="watchlist"
          className="flex flex-col flex-1 overflow-hidden"
        >
          {watchlistMarkets.length === 0 ? (
            <WatchlistEmptyState />
          ) : (
            <div className="flex flex-col h-full overflow-hidden">
              <WatchlistHeader
                marketCount={watchlistMarkets.length}
                sortCriteria={sortCriteria}
                onSort={handleSort}
              />

              <div className="overflow-y-auto flex-1 min-h-0 scrollbar-none">
                <MarketList
                  markets={watchlistMarkets}
                  isLoading={false}
                  selectedMarket={getSelectedMarket()}
                  onMarketSelect={(symbol) => {
                    const market = watchlistMarkets.find(
                      (m) => m.symbol === symbol
                    );
                    if (market && market.type) {
                      setMarketType(market.type);
                      handleMarketSelect(symbol);
                    }
                  }}
                  onToggleWatchlist={toggleWatchlist}
                  marketType={marketType}
                />
              </div>
            </div>
          )}
        </TabContent>

        <TabContent
          value="stocks"
          className="flex flex-col flex-1 overflow-hidden"
        >
          <SearchBar
            placeholder="Search stocks..."
            value={searchQuery}
            onChange={setSearchQuery}
          />

          <ColumnHeaders
            leftColumn={{ label: "Symbol", sortField: "name" }}
            rightColumn={{ label: "24h Change", sortField: "change" }}
            sortCriteria={sortCriteria}
            onSort={handleSort}
          />

          <div className="overflow-y-auto flex-1 min-h-0 scrollbar-none">
            <MarketList
              markets={filteredMarkets}
              isLoading={isStocksLoading}
              selectedMarket={stocksSelectedMarket}
              onMarketSelect={handleMarketSelect}
              onToggleWatchlist={toggleWatchlist}
              marketType="stocks"
              onSortVolume={() => handleSort("volume")}
              onSortPrice={() => handleSort("price")}
            />
          </div>
        </TabContent>

        <TabContent
          value="feed"
          className="flex flex-col flex-1 overflow-hidden"
        >
          <SearchBar
            placeholder="Search Mkts..."
            value={searchQuery}
            onChange={setSearchQuery}
          />

          <ColumnHeaders
            leftColumn={{ label: "Symbol", sortField: "name" }}
            rightColumn={{ label: "24h Change", sortField: "change" }}
            sortCriteria={sortCriteria}
            onSort={handleSort}
          />

          <div className="overflow-y-auto flex-1 min-h-0 scrollbar-none">
            <MarketList
              markets={filteredMarkets}
              isLoading={isFeedLoading}
              selectedMarket={feedSelectedMarket}
              onMarketSelect={handleMarketSelect}
              onToggleWatchlist={toggleWatchlist}
              marketType="feed"
              onSortVolume={() => handleSort("volume")}
              onSortPrice={() => handleSort("price")}
            />
          </div>
        </TabContent>
      </Tabs>
    </div>
  );
}
